import type { AuthUser } from '@/api/models';
import type { AuthMethod } from '@/utils/authSession';

/**
 * Who may see and change the chart of accounts.
 *
 * This mirrors the two bos-api policies, and both carry a constraint that is
 * easy to miss: they require `auth_method=password` **as well as** a role. A
 * BranchAdmin holding a PIN-issued kiosk token is a BranchAdmin, and still
 * receives 403 on every one of these endpoints.
 *
 * | | policy | this client |
 * | --- | --- | --- |
 * | read | `BranchAdmin` = OrgAdmin\|BranchAdmin + password | `readOnly` / `full` |
 * | write | `OrgAdmin` = OrgAdmin + password | `full` |
 *
 * OPEN ITEM (reported): the brief states reads for BranchAdmin and writes for
 * OrgAdmin without saying explicitly that OrgAdmin also reads. A write-only role
 * would be incoherent — you cannot pick a parent account you cannot list — so
 * OrgAdmin is treated as read **and** write here. If the policy really is
 * write-only, this is the line that changes.
 *
 * Deriving access here — rather than letting the page discover it from a 403 —
 * is what lets the screen explain *why* it is restricted. A raw 403 tells the
 * user nothing they can act on. This is the same shape as `settingsAccess`,
 * deliberately: two surfaces with the same policy should not have two different
 * ways of expressing it.
 */
export type AccountsAccess =
    /**
     * A PIN/kiosk session. Every accounts endpoint will 403 whatever the role,
     * so the page must not fetch at all — it explains that the chart needs a
     * password sign-in.
     */
    | { kind: 'kiosk' }
    /** Signed in, but the role does not permit even reading (e.g. a Seller). */
    | { kind: 'forbidden' }
    /** BranchAdmin: the whole chart is readable, nothing is writable. */
    | { kind: 'readOnly' }
    /** OrgAdmin with a password session: full read and write. */
    | { kind: 'full' };

/**
 * Derive the access level from the persisted session.
 *
 * Fails closed on every unknown: no user, or an `auth_method` that could not be
 * read, is treated as a kiosk session rather than optimistically rendering a
 * page whose every request would 403.
 */
export const deriveAccountsAccess = (
    user: AuthUser | null,
    authMethod: AuthMethod | null,
): AccountsAccess => {
    if (!user) {
        return { kind: 'kiosk' };
    }
    // Unknown is not password. Rendering the chart and letting each request 403
    // is strictly worse than saying why the page is unavailable.
    if (authMethod !== 'password') {
        return { kind: 'kiosk' };
    }
    switch (user.role) {
        case 'OrgAdmin':
            return { kind: 'full' };
        case 'BranchAdmin':
            return { kind: 'readOnly' };
        default:
            return { kind: 'forbidden' };
    }
};

/** Whether this access level may read the chart at all. */
export const canReadAccounts = (access: AccountsAccess): boolean =>
    access.kind === 'full' || access.kind === 'readOnly';

/** Whether this access level may add, edit, or archive an account. */
export const canWriteAccounts = (access: AccountsAccess): boolean =>
    access.kind === 'full';

/** i18n key (namespace `pages`) explaining why the page is restricted. */
export const ACCESS_NOTICE_KEY: Record<
    Exclude<AccountsAccess['kind'], 'full'>,
    { title: string; message: string }
> = {
    kiosk: {
        title: 'chartOfAccounts.access.kiosk.title',
        message: 'chartOfAccounts.access.kiosk.message',
    },
    forbidden: {
        title: 'chartOfAccounts.access.forbidden.title',
        message: 'chartOfAccounts.access.forbidden.message',
    },
    readOnly: {
        title: 'chartOfAccounts.access.readOnly.title',
        message: 'chartOfAccounts.access.readOnly.message',
    },
};
